import React from "react";
import { CurrencyCircleDollar, ArrowRight } from "@phosphor-icons/react";

const ROLE_LABEL = {
  attraction: "Attraction offer",
  upsell: "Upsell",
  downsell: "Downsell",
  continuity: "Continuity",
};

const ROLE_BLURB = {
  attraction: "Turns a stranger into a buyer",
  upsell: "Offered the moment they say yes",
  downsell: "For the ones who say no",
  continuity: "Keeps the result alive",
};

/**
 * The offer SEQUENCE, not a single offer — and the 30-day arithmetic that says
 * whether new customers can be paid for out of the customers you just won.
 */
export default function MoneyModel({ model }) {
  if (!model || !model.steps || model.steps.length === 0) return null;
  const financed = model.customer_financed;

  return (
    <section className="mt-20" data-testid="money-model-section">
      <div className="flex items-center gap-3 mb-2">
        <CurrencyCircleDollar weight="fill" className="h-5 w-5 text-[#C9A961]" />
        <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight">
          The money model
        </h2>
      </div>
      <p className="text-[#9AA5B5] max-w-3xl mb-8">
        One offer is a transaction. A sequence is a business. This is what you
        put in front of a buyer, in what order, and what it throws off.
      </p>

      <div className="grid grid-cols-12 gap-4">
        {model.steps.map((s, i) => (
          <div
            key={`${s.role}-${i}`}
            className="col-span-12 md:col-span-6 lg:col-span-3 bg-[#0F1F38] border border-white/5 rounded-lg p-6 flex flex-col"
            data-testid={`money-step-${s.role}`}
          >
            <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#C9A961] font-bold mb-1">
              {ROLE_LABEL[s.role] || s.role}
              {i < model.steps.length - 1 && (
                <ArrowRight weight="bold" className="h-3 w-3 opacity-50" />
              )}
            </div>
            <div className="text-[11px] text-[#808A9B] mb-4">
              {ROLE_BLURB[s.role] || ""}
            </div>
            <div className="font-display font-bold text-white text-lg mb-2">
              {s.name}
            </div>
            <p className="text-sm text-[#9AA5B5] leading-relaxed flex-1">
              {s.what_it_is}
            </p>
            <div className="mt-5 pt-4 border-t border-white/5 flex items-baseline justify-between">
              <span className="font-display text-2xl text-[#C9A961]">
                {s.price_display}
              </span>
              <span className="text-xs text-[#808A9B]">
                {s.take_rate_pct}% take
              </span>
            </div>
            {s.when_offered && (
              <div className="mt-2 text-[11px] text-[#808A9B]">
                {s.when_offered}
              </div>
            )}
          </div>
        ))}
      </div>

      <div
        className={`mt-6 rounded-lg p-8 border ${
          financed
            ? "bg-[#0F1F38] border-[#C9A961]/40"
            : "bg-[#0F1F38] border-[#E0B64B]/40"
        }`}
        data-testid="money-model-payback"
      >
        <div className="text-xs tracking-[0.25em] uppercase text-[#C9A961] font-bold mb-4">
          First 30 days, per buyer
        </div>
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-6 md:col-span-3">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#808A9B] mb-1">
              Revenue
            </div>
            <div className="font-display text-2xl text-[#F5F1E8]">
              {model.revenue_per_buyer_30d}
            </div>
          </div>
          <div className="col-span-6 md:col-span-3">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#808A9B] mb-1">
              Gross profit
            </div>
            <div className="font-display text-2xl text-[#C9A961] font-black">
              {model.gross_profit_per_buyer_30d}
            </div>
          </div>
          <div className="col-span-12 md:col-span-6">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#808A9B] mb-1">
              Most you can pay to win the next customer
            </div>
            <div className="font-display text-2xl text-[#F5F1E8]">
              {model.allowed_cac}
            </div>
          </div>
        </div>
        <p className="mt-6 text-[#F5F1E8] leading-relaxed">
          {model.payback_note}
        </p>
        {model.assumptions && model.assumptions.length > 0 && (
          <div className="mt-6 pt-6 border-t border-white/5">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#808A9B] mb-2">
              Only true if
            </div>
            <ul className="space-y-1">
              {model.assumptions.map((a, i) => (
                <li key={i} className="text-sm text-[#9AA5B5]">
                  — {a}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}
